import { useState, useEffect } from "react";
import { useRouter } from "next/router";

export default function LoginPage() {
  const router = useRouter();
  const [isSetup, setIsSetup] = useState<boolean | null>(null);
  const [setupUnavailable,setSetupUnavailable]=useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if this is a first-run setup
    fetch("/api/auth/setup", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) })
      .then(async r => ({status:r.status,data:await r.json()}))
      .then(({status,data}) => {
        if(status===503){setSetupUnavailable(true);setIsSetup(false);}
        else setIsSetup(data.error === "Setup already complete. Use the Team panel to add users." ? false : true);
      })
      .catch(() => setIsSetup(false));
  }, []);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await fetch("/api/auth/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, password }) });
      const d = await r.json();
      if (!r.ok) { setError(d.error || "Login failed"); return; }
      const next = router.query.next as string;
      router.replace(next && next !== "" ? next : "/");
    } catch { setError("Connection error — try again"); }
    finally { setLoading(false); }
  }

  async function handleSetup(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      const r = await fetch("/api/auth/setup", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, password }) });
      const d = await r.json();
      if (!r.ok) { setError(d.error || "Setup failed"); return; }
      // Now log in
      const lr = await fetch("/api/auth/login", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ username, password }) });
      if (lr.ok) router.replace("/");
    } catch { setError("Connection error — try again"); }
    finally { setLoading(false); }
  }

  const inp: React.CSSProperties = { width: "100%", background: "#18181f", border: "1px solid #2a2a3a", borderRadius: 8, color: "#e4e4f0", fontFamily: "inherit", fontSize: 14, padding: "10px 13px", outline: "none", boxSizing: "border-box" };
  const pbtn: React.CSSProperties = { width: "100%", background: "#5b6ef5", color: "#fff", border: "none", borderRadius: 8, padding: "11px", cursor: "pointer", fontSize: 14, fontWeight: 600, fontFamily: "inherit" };

  return (
    <div style={{ fontFamily: "'DM Sans',system-ui,sans-serif", background: "#0a0a0f", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <style>{`*{box-sizing:border-box}body{margin:0}input:focus{border-color:#5b6ef5!important;outline:none}`}</style>
      <div style={{ width: "100%", maxWidth: 380 }}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 36, justifyContent: "center" }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: "linear-gradient(135deg,#5b6ef5,#8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>✦</div>
          <span style={{ fontSize: "1.2rem", fontWeight: 700, color: "#e4e4f0" }}>Blackbird Content Engine</span>
        </div>

        <div style={{ background: "#111118", border: "1px solid #2a2a3a", borderRadius: 12, padding: 28 }}>
          {isSetup === null && (
            <div style={{ textAlign: "center", color: "#55556a", padding: "24px 0" }}>Loading…</div>
          )}

          {setupUnavailable && (
            <div role="alert" style={{fontSize:13,lineHeight:1.7,color:"#cdd4ec",padding:"10px 0"}}>
              This preview has no account database, so creating an account here would not work.
              <p style={{marginBottom:0}}>Use your existing team login on the <a href="https://content.blackbird-marketing.uk/login" style={{color:"#aeb7ff"}}>main Content Tool</a>. The pilot must be released to the main workspace or connected to an isolated preview database before you can use it.</p>
            </div>
          )}
          {!setupUnavailable && isSetup === true && (
            <>
              <h2 style={{ margin: "0 0 6px", fontSize: "1.1rem", fontWeight: 700, color: "#e4e4f0" }}>Create your account</h2>
              <p style={{ margin: "0 0 24px", fontSize: 13, color: "#55556a" }}>First-time setup — this creates the admin account.</p>
              <form onSubmit={handleSetup}>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: "block", fontSize: 12, color: "#9090b0", marginBottom: 5 }}>Username</label>
                  <input style={inp} value={username} onChange={e => setUsername(e.target.value)} placeholder="e.g. alice" autoFocus required />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: "block", fontSize: 12, color: "#9090b0", marginBottom: 5 }}>Password</label>
                  <input style={inp} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" required />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 12, color: "#9090b0", marginBottom: 5 }}>Confirm password</label>
                  <input style={inp} type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" required />
                </div>
                {error && <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 12, padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 6 }}>{error}</div>}
                <button type="submit" style={pbtn} disabled={loading}>{loading ? "Creating…" : "Create account & sign in"}</button>
              </form>
            </>
          )}

          {!setupUnavailable && isSetup === false && (
            <>
              <h2 style={{ margin: "0 0 6px", fontSize: "1.1rem", fontWeight: 700, color: "#e4e4f0" }}>Sign in</h2>
              <p style={{ margin: "0 0 24px", fontSize: 13, color: "#55556a" }}>Team access only.</p>
              <form onSubmit={handleLogin}>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: "block", fontSize: 12, color: "#9090b0", marginBottom: 5 }}>Username</label>
                  <input style={inp} value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" autoFocus required />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 12, color: "#9090b0", marginBottom: 5 }}>Password</label>
                  <input style={inp} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required />
                </div>
                {error && <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 12, padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 6 }}>{error}</div>}
                <button type="submit" style={pbtn} disabled={loading}>{loading ? "Signing in…" : "Sign in"}</button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
